import React, { useState, useEffect, useRef, useCallback } from "react";
import { getPyodide } from "../utils/pyodideLoader";
import {
  Box,
  Stack,
  Button,
  Select,
  MenuItem,
  Typography,
  useTheme,
  Snackbar,
  Alert,
  Tooltip,
  IconButton,
  Chip,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadIcon from "@mui/icons-material/Download";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import TerminalIcon from "@mui/icons-material/Terminal";

import HtmlCodeEditorInput from "./codeEditor/HtmlCodeEditorInput";
import HtmlCodeEditorOutput from "./codeEditor/HtmlCodeEditorOutput";
import JSCodeEditorInput from "./codeEditor/JSCodeEditorInput";
import JSCodeEditorOutput from "./codeEditor/JSCodeEditorOutput";
import PythonCodeEditorInput from "./codeEditor/PythonCodeEditorInput";
import PythonCodeEditorOutput from "./codeEditor/PythonCodeEditorOutput";
import RubyCodeEditorInput from "./codeEditor/RubyCodeEditorInput";
import RubyCodeEditorOutput from "./codeEditor/RubyCodeEditorOutput";
import LuaCodeEditorInput from "./codeEditor/LuaCodeEditorInput";
import LuaCodeEditorOutput from "./codeEditor/LuaCodeEditorOutput";
import SqlCodeEditorInput from "./codeEditor/SqlCodeEditorInput";
import SqlCodeEditorOutput from "./codeEditor/SqlCodeEditorOutput";

const LANGUAGE_CONFIG = {
  python: { label: "Python", icon: "🐍", ext: "py", runtime: "Python 3.11 (Pyodide Wasm)" },
  javascript: { label: "JavaScript", icon: "⚡", ext: "js", runtime: "Browser V8 Engine" },
  html: { label: "HTML", icon: "🌐", ext: "html", runtime: "Live DOM Renderer" },
  css: { label: "CSS", icon: "🎨", ext: "css", runtime: "CSS Style Engine" },
  sql: { label: "SQL", icon: "🗄️", ext: "sql", runtime: "SQLite (WebAssembly)" },
  ruby: { label: "Ruby", icon: "💎", ext: "rb", runtime: "Opal Ruby Runtime" },
  lua: { label: "Lua", icon: "🌙", ext: "lua", runtime: "Fengari Lua Wasm" },
};

// Helper ensuring line numbers stretch the full height of the page by default (~26 lines)
const ensureMinLines = (codeStr, minLines = 26) => {
  if (typeof codeStr !== "string") return "";
  const lines = codeStr.split("\n");
  if (lines.length >= minLines) return codeStr;
  return codeStr + "\n".repeat(minLines - lines.length);
};

const defaultCodeMap = {
  python: ensureMinLines(`# Python 3.11 Playground (runs directly in your browser)
def greet(name):
    return f"Hello, {name}! Welcome to CodingComet."

print(greet("Developer"))

# Example: List modification
def modify_list(items):
    items[0] = "Modified"
    return items

my_list = [10, 20, 30]
print("Original:", my_list)
modify_list(my_list)
print("Updated:", my_list)`),

  javascript: ensureMinLines(`// JavaScript Playground (ES6+)
function greet(name) {
  return \`Hello, \${name}! Welcome to CodingComet.\`;
}

console.log(greet("Developer"));

const numbers = [1, 2, 3, 4, 5];
const squared = numbers.map((n) => n ** 2);
console.log("Squared numbers:", squared);`),

  html: ensureMinLines(`<!DOCTYPE html>
<html>
<head>
  <style>
    body { font-family: sans-serif; text-align: center; padding: 40px; background: #0f172a; color: #f8fafc; }
    h1 { color: #38bdf8; }
    .card { background: #1e293b; padding: 24px; border-radius: 12px; display: inline-block; box-shadow: 0 4px 20px rgba(0,0,0,0.3); }
  </style>
</head>
<body>
  <div class="card">
    <h1>🚀 CodingComet Playground</h1>
    <p>Edit HTML & CSS and see real-time updates!</p>
  </div>
</body>
</html>`),

  css: ensureMinLines(`body {
  margin: 0;
  padding: 2rem;
  background: linear-gradient(135deg, #1e1e2f, #12121c);
  color: #fff;
  font-family: 'Inter', sans-serif;
  text-align: center;
}`),

  sql: ensureMinLines(`-- Interactive SQLite in WebAssembly
SELECT * FROM employees;`),

  ruby: ensureMinLines(`def greet(name)
  "Hello, #{name}! Welcome to CodingComet."
end

puts greet("Developer")`),

  lua: ensureMinLines(`function greet(name)
    return "Hello, " .. name .. "! Welcome to CodingComet."
end

print(greet("Developer"))`),
};

export default function OnlineCodePlayground() {
  const [language, setLanguage] = useState("python");
  const [codeMap, setCodeMap] = useState(() => {
    try {
      const saved = localStorage.getItem("playgroundCodeMap");
      if (saved) {
        const parsed = JSON.parse(saved);
        const merged = { ...defaultCodeMap, ...parsed };
        Object.keys(merged).forEach((k) => {
          merged[k] = ensureMinLines(merged[k], 26);
        });
        return merged;
      }
    } catch (e) {
      console.warn("Failed to load saved code:", e);
    }
    return defaultCodeMap;
  });

  const [code, setCode] = useState(() => codeMap.python || defaultCodeMap.python);
  const [executedCode, setExecutedCode] = useState(null);
  const [pyodide, setPyodide] = useState(null);
  const [opal, setOpal] = useState(null);
  const [luaRuntime, setLuaRuntime] = useState(null);
  const [sqlDb, setSqlDb] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dividerX, setDividerX] = useState(50);
  const [isResizing, setIsResizing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  const containerRef = useRef(null);

  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  // Lazy-load Python runtime (Pyodide)
  useEffect(() => {
    if (language !== "python" || pyodide) return;
    let isMounted = true;
    const loadPyodideRuntime = async () => {
      try {
        const py = await getPyodide();
        if (isMounted) setPyodide(py);
      } catch (error) {
        console.error("Failed to load Pyodide:", error);
      }
    };
    loadPyodideRuntime();
    return () => { isMounted = false; };
  }, [language, pyodide]);

  // Lazy-load other runtimes
  useEffect(() => {
    if (language === "ruby" && !opal) {
      if (window.Opal) {
        setOpal(window.Opal);
      } else {
        const script = document.createElement("script");
        script.src = "https://cdn.jsdelivr.net/npm/opal@latest/build/opal.min.js";
        script.onload = () => { if (window.Opal) setOpal(window.Opal); };
        document.head.appendChild(script);
      }
    }

    if (language === "lua" && !luaRuntime) {
      if (window.fengari) {
        setLuaRuntime(window.fengari);
      } else {
        const script = document.createElement("script");
        script.src = "https://cdn.jsdelivr.net/npm/fengari-web@latest/dist/fengari-web.js";
        script.onload = () => { if (window.fengari) setLuaRuntime(window.fengari); };
        document.head.appendChild(script);
      }
    }

    if (language === "sql" && !sqlDb) {
      const loadSqlJs = async () => {
        try {
          if (window.initSqlJs) {
            const SQL = await window.initSqlJs({
              locateFile: (file) => `https://sql.js.org/dist/${file}`,
            });
            const database = new SQL.Database();
            database.run(`
              CREATE TABLE employees (id INTEGER PRIMARY KEY, name TEXT, role TEXT, salary REAL);
              INSERT INTO employees VALUES (1, 'Alice', 'Developer', 75000);
              INSERT INTO employees VALUES (2, 'Bob', 'Designer', 68000);
              INSERT INTO employees VALUES (3, 'Charlie', 'Manager', 95000);
              INSERT INTO employees VALUES (4, 'Diana', 'Developer', 72000);
              INSERT INTO employees VALUES (5, 'Eve', 'Designer', 69000);
            `);
            setSqlDb(database);
          } else {
            const script = document.createElement("script");
            script.src = "https://sql.js.org/dist/sql-wasm.js";
            script.onload = async () => {
              if (window.initSqlJs) {
                const SQL = await window.initSqlJs({
                  locateFile: (file) => `https://sql.js.org/dist/${file}`,
                });
                const database = new SQL.Database();
                database.run(`
                  CREATE TABLE employees (id INTEGER PRIMARY KEY, name TEXT, role TEXT, salary REAL);
                  INSERT INTO employees VALUES (1, 'Alice', 'Developer', 75000);
                  INSERT INTO employees VALUES (2, 'Bob', 'Designer', 68000);
                  INSERT INTO employees VALUES (3, 'Charlie', 'Manager', 95000);
                  INSERT INTO employees VALUES (4, 'Diana', 'Developer', 72000);
                  INSERT INTO employees VALUES (5, 'Eve', 'Designer', 69000);
                `);
                setSqlDb(database);
              }
            };
            document.head.appendChild(script);
          }
        } catch (error) {
          console.error("Failed to load SQL.js:", error);
        }
      };
      loadSqlJs();
    }
  }, [language, opal, luaRuntime, sqlDb]);

  // Execute Code
  const runCode = useCallback(() => {
    if (language === "python" && !pyodide) {
      setSnackbar({
        open: true,
        message: "⏳ Python runtime (Pyodide) is loading... Please wait a few seconds.",
        severity: "warning",
      });
      return;
    }
    if (language === "ruby" && !opal) {
      setSnackbar({
        open: true,
        message: "⏳ Ruby runtime is loading... Please wait.",
        severity: "warning",
      });
      return;
    }
    if (language === "lua" && !luaRuntime) {
      setSnackbar({
        open: true,
        message: "⏳ Lua runtime is loading... Please wait.",
        severity: "warning",
      });
      return;
    }
    if (language === "sql" && !sqlDb) {
      setSnackbar({
        open: true,
        message: "⏳ SQL runtime is loading... Please wait.",
        severity: "warning",
      });
      return;
    }

    setLoading(true);
    setExecutedCode(code);
    setTimeout(() => setLoading(false), 150);
  }, [language, pyodide, opal, luaRuntime, sqlDb, code]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setSnackbar({
        open: true,
        message: "Code copied to clipboard!",
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to copy code",
        severity: "error",
      });
    }
  };

  const handleDownload = () => {
    const ext = LANGUAGE_CONFIG[language]?.ext || "txt";
    const blob = new Blob([code], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `main.${ext}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    setSnackbar({
      open: true,
      message: `Downloaded main.${ext} successfully!`,
      severity: "success",
    });
  };

  const handleClear = () => {
    setExecutedCode("");
  };

  const handleResetCode = () => {
    const starter = ensureMinLines(defaultCodeMap[language] || "", 26);
    handleCodeChange(starter);
    setSnackbar({
      open: true,
      message: `Reset ${LANGUAGE_CONFIG[language]?.label || language} code to starter template.`,
      severity: "info",
    });
  };

  const handleCodeChange = (value) => {
    setCode(value);
    setCodeMap((prev) => {
      const updated = { ...prev, [language]: value };
      try {
        localStorage.setItem("playgroundCodeMap", JSON.stringify(updated));
      } catch (e) {
        console.warn("Failed to save code:", e);
      }
      return updated;
    });
  };

  const handleLanguageChange = (e) => {
    const lang = e.target.value;
    setLanguage(lang);
    const newCode = ensureMinLines(codeMap[lang] || defaultCodeMap[lang] || "", 26);
    setCode(newCode);
    setExecutedCode(null);
  };

  // Draggable Splitter
  const handleMouseDown = () => setIsResizing(true);
  const handleMouseUp = () => setIsResizing(false);
  const handleMouseMove = (e) => {
    if (!isResizing || !containerRef.current) return;
    const container = containerRef.current.getBoundingClientRect();
    const newX = ((e.clientX - container.left) / container.width) * 100;
    setDividerX(Math.min(Math.max(newX, 20), 80));
  };

  // Keyboard shortcut: Ctrl + Enter / Cmd + Enter to Run
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
        e.preventDefault();
        runCode();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [runCode]);

  // Sync code ONLY when language changes
  useEffect(() => {
    setCode(ensureMinLines(codeMap[language] || defaultCodeMap[language] || "", 26));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  const isRuntimeLoading =
    (language === "python" && !pyodide) ||
    (language === "ruby" && !opal) ||
    (language === "lua" && !luaRuntime) ||
    (language === "sql" && !sqlDb);

  return (
    <Box
      ref={containerRef}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      sx={{
        width: "100%",
        height: { xs: "calc(100dvh - 56px)", md: "calc(100vh - 64px)" },
        display: "flex",
        flexDirection: "column",
        backgroundColor: isDark ? "#0d1117" : "#f6f8fa",
        overflow: "hidden",
        userSelect: isResizing ? "none" : "auto",
      }}
    >
      {/* 💻 Main Workspace: Split Pane (Editor + Terminal) with Unified Single Header */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        sx={{
          flex: 1,
          height: "100%",
          overflow: "hidden",
        }}
      >
        {/* LEFT PANE: CODE EDITOR (with Single Header on Input Side) */}
        <Box
          sx={{
            width: { md: `${dividerX}%`, xs: "100%" },
            height: { xs: "50%", md: "100%" },
            display: "flex",
            flexDirection: "column",
            backgroundColor: isDark ? "#0d1117" : "#ffffff",
            overflow: "hidden",
          }}
        >
          {/* ⚡ SINGLE HEADER (INPUT CODE SIDE) */}
          <Box
            sx={{
              height: 44,
              minHeight: 44,
              px: { xs: 1, sm: 1.5 },
              backgroundColor: isDark ? "#161b22" : "#f1f3f5",
              borderBottom: `1px solid ${theme.palette.divider}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              zIndex: 2,
            }}
          >
            {/* Left: Language Select + File Tab */}
            <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0, overflow: "hidden" }}>
              <Select
                size="small"
                value={language}
                onChange={handleLanguageChange}
                sx={{
                  fontSize: "0.84rem",
                  fontWeight: 600,
                  borderRadius: "6px",
                  backgroundColor: isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.04)",
                  height: 32,
                  "& .MuiSelect-select": {
                    py: 0.5,
                    px: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.8,
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: theme.palette.divider,
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                {Object.entries(LANGUAGE_CONFIG).map(([key, config]) => (
                  <MenuItem key={key} value={key} sx={{ fontSize: "0.85rem", display: "flex", gap: 1 }}>
                    <span>{config.icon}</span> <span>{config.label}</span>
                  </MenuItem>
                ))}
              </Select>

              <Box
                sx={{
                  display: { xs: "none", sm: "inline-flex" },
                  alignItems: "center",
                  gap: 0.6,
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  backgroundColor: isDark ? "#0d1117" : "#ffffff",
                  px: 1.2,
                  py: 0.4,
                  borderRadius: "6px",
                  border: `1px solid ${theme.palette.divider}`,
                  whiteSpace: "nowrap",
                }}
              >
                <span>main.{LANGUAGE_CONFIG[language]?.ext || "txt"}</span>
              </Box>
            </Stack>

            {/* Right: Code Actions (Reset, Copy, Download, Run on INPUT side!) */}
            <Stack direction="row" alignItems="center" spacing={0.6}>
              <Tooltip title="Reset to Starter Template">
                <IconButton
                  size="small"
                  onClick={handleResetCode}
                  sx={{
                    color: isDark ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.65)",
                    p: 0.6,
                    "&:hover": { color: theme.palette.primary.main },
                  }}
                >
                  <RestartAltIcon sx={{ fontSize: "1.15rem" }} />
                </IconButton>
              </Tooltip>

              <Tooltip title="Copy Code">
                <IconButton
                  size="small"
                  onClick={handleCopy}
                  sx={{
                    color: isDark ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.65)",
                    p: 0.6,
                    "&:hover": { color: theme.palette.primary.main },
                  }}
                >
                  <ContentCopyIcon sx={{ fontSize: "1.05rem" }} />
                </IconButton>
              </Tooltip>

              {/* ⬇ DOWNLOAD BUTTON ON INPUT CODE SIDE */}
              <Tooltip title={`Download main.${LANGUAGE_CONFIG[language]?.ext || "txt"}`}>
                <IconButton
                  size="small"
                  onClick={handleDownload}
                  sx={{
                    color: isDark ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.65)",
                    p: 0.6,
                    "&:hover": { color: theme.palette.primary.main },
                  }}
                >
                  <DownloadIcon sx={{ fontSize: "1.15rem" }} />
                </IconButton>
              </Tooltip>

              {/* ▶ RUN BUTTON (ON INPUT SIDE) */}
              <Button
                variant="contained"
                size="small"
                onClick={runCode}
                disabled={loading || isRuntimeLoading}
                startIcon={<PlayArrowIcon sx={{ fontSize: "1.1rem" }} />}
                sx={{
                  background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                  color: "#ffffff",
                  fontWeight: 700,
                  fontSize: "0.82rem",
                  textTransform: "none",
                  borderRadius: "6px",
                  height: 32,
                  px: 1.5,
                  boxShadow: "0 2px 8px rgba(16, 185, 129, 0.35)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
                    boxShadow: "0 4px 12px rgba(16, 185, 129, 0.45)",
                  },
                  "&.Mui-disabled": {
                    background: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
                    color: isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)",
                  },
                }}
              >
                {loading ? "Running..." : "Run"}
                <Box
                  component="span"
                  sx={{
                    ml: 0.8,
                    fontSize: "0.68rem",
                    opacity: 0.85,
                    backgroundColor: "rgba(0,0,0,0.25)",
                    px: 0.5,
                    py: 0.1,
                    borderRadius: "4px",
                    display: { xs: "none", lg: "inline" },
                  }}
                >
                  Ctrl+↵
                </Box>
              </Button>
            </Stack>
          </Box>

          {/* Active CodeMirror Component (fills full remaining height) */}
          <Box
            sx={{
              flex: 1,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }}
          >
            {language === "html" && (
              <HtmlCodeEditorInput initialCode={code} onCodeChange={handleCodeChange} />
            )}
            {language === "css" && (
              <HtmlCodeEditorInput initialCode={code} onCodeChange={handleCodeChange} />
            )}
            {language === "javascript" && (
              <JSCodeEditorInput initialCode={code} onCodeChange={handleCodeChange} />
            )}
            {language === "python" && (
              <PythonCodeEditorInput initialCode={code} onCodeChange={handleCodeChange} editable={true} />
            )}
            {language === "ruby" && (
              <RubyCodeEditorInput initialCode={code} onCodeChange={handleCodeChange} editable={true} />
            )}
            {language === "lua" && (
              <LuaCodeEditorInput initialCode={code} onCodeChange={handleCodeChange} editable={true} />
            )}
            {language === "sql" && (
              <SqlCodeEditorInput initialCode={code} onCodeChange={handleCodeChange} editable={true} />
            )}
          </Box>
        </Box>

        {/* ↔ DRAGGABLE SPLITTER / DIVIDER */}
        <Box
          onMouseDown={handleMouseDown}
          onDoubleClick={() => setDividerX(50)}
          sx={{
            width: { md: "8px", xs: "100%" },
            height: { xs: "8px", md: "100%" },
            backgroundColor: isResizing
              ? theme.palette.primary.main
              : isDark
              ? "rgba(255, 255, 255, 0.08)"
              : "rgba(0, 0, 0, 0.08)",
            cursor: { md: "col-resize", xs: "row-resize" },
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "background-color 0.2s ease",
            "&:hover": {
              backgroundColor: theme.palette.primary.main,
            },
            "&::after": {
              content: '""',
              width: { md: "3px", xs: "24px" },
              height: { md: "24px", xs: "3px" },
              borderRadius: "3px",
              backgroundColor: isDark ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.3)",
            },
          }}
        />

        {/* RIGHT PANE: OUTPUT TERMINAL (with Single Header on Output Side) */}
        <Box
          sx={{
            width: { md: `${100 - dividerX}%`, xs: "100%" },
            height: { xs: "50%", md: "100%" },
            display: "flex",
            flexDirection: "column",
            backgroundColor: isDark ? "#0d1117" : "#ffffff",
            overflow: "hidden",
          }}
        >
          {/* ⚡ SINGLE HEADER (OUTPUT SIDE) */}
          <Box
            sx={{
              height: 44,
              minHeight: 44,
              px: { xs: 1, sm: 1.5 },
              backgroundColor: isDark ? "#161b22" : "#f1f3f5",
              borderBottom: `1px solid ${theme.palette.divider}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              zIndex: 2,
            }}
          >
            {/* Title & Runtime Status */}
            <Stack direction="row" alignItems="center" spacing={1}>
              <TerminalIcon sx={{ fontSize: "1.1rem", color: isDark ? "#58a6ff" : "#0969da" }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: "0.85rem" }}>
                Console Output
              </Typography>

              {/* Runtime Status Pill */}
              <Chip
                size="small"
                icon={
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      backgroundColor: isRuntimeLoading ? "#eab308" : "#22c55e",
                      boxShadow: isRuntimeLoading
                        ? "0 0 6px #eab308"
                        : "0 0 6px #22c55e",
                      ml: 0.8,
                    }}
                  />
                }
                label={isRuntimeLoading ? "Loading..." : LANGUAGE_CONFIG[language]?.runtime || "Ready"}
                sx={{
                  height: 24,
                  fontSize: "0.72rem",
                  fontWeight: 500,
                  backgroundColor: isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.04)",
                  border: `1px solid ${theme.palette.divider}`,
                  display: { xs: "none", sm: "inline-flex" },
                }}
              />
            </Stack>

            {/* Clear Console Action */}
            <Tooltip title="Clear Console">
              <IconButton
                size="small"
                onClick={handleClear}
                sx={{
                  color: isDark ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.6)",
                  p: 0.6,
                  "&:hover": { color: "#f85149" },
                }}
              >
                <DeleteOutlineIcon sx={{ fontSize: "1.15rem" }} />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Terminal Output Area */}
          <Box
            sx={{
              flex: 1,
              overflow: "hidden",
              minHeight: 0,
              p: 1.5,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {language === "python" ? (
              <PythonCodeEditorOutput
                code={executedCode}
                pyodide={pyodide}
                loading={loading}
              />
            ) : language === "ruby" ? (
              <RubyCodeEditorOutput
                code={executedCode}
                opal={opal}
                loading={loading}
              />
            ) : language === "lua" ? (
              <LuaCodeEditorOutput
                code={executedCode}
                lua={luaRuntime}
                loading={loading}
              />
            ) : language === "javascript" ? (
              <JSCodeEditorOutput
                code={executedCode}
                loading={loading}
              />
            ) : language === "sql" ? (
              <SqlCodeEditorOutput
                code={executedCode}
                sqlDb={sqlDb}
                loading={loading}
              />
            ) : (
              <HtmlCodeEditorOutput
                code={executedCode}
                language={language}
              />
            )}
          </Box>
        </Box>
      </Stack>

      {/* Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%", borderRadius: "8px", fontWeight: 500 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
